import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSEO } from '../hooks/useSEO';
import { supabase } from '../lib/supabaseClient';
import { UserType, OnboardingData } from '../types/user';
import { FormValidation } from '../components/ui';
import { Alert } from '../components/ui';
import { AnimatedCounter } from '../components/ui';
import LearningStyleAssessment from '../components/LearningStyleAssessment';
import ModernButton from '../components/ModernButton';
import {
  UserCircle,
  Globe,
  Calendar,
  Users,
  MapPin,
  FileText,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  GraduationCap,
  Settings,
  BookOpen,
  Bell,
  Shield,
  Palette,
  Save,
  ArrowLeft,
  Edit,
  Check,
  X,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Key,
  Mail,
  Phone,
  CreditCard,
  Home,
  Briefcase,
  Heart,
  Target,
  Award,
  TrendingUp,
  BarChart3,
  Star,
  Crown,
  Gem,
  Sparkles,
  Sun,
  Moon,
  Cloud,
  Wind,
  Droplets,
  Flame,
  Snowflake,
  TreePine,
  Flower,
  Leaf,
  Bug,
  Fish,
  Bird,
  Cat,
  Dog,
  Rabbit,
  Turtle,
  Butterfly,
  Bee,
  Ant,
  Spider,
  Snail,
  Frog,
  Lizard,
  Snake,
  Dragon,
  Unicorn,
  Rainbow,
  Zap,
  Lightbulb,
  Info,
  HelpCircle,
  Search,
  Filter,
  Plus,
  Archive,
  Pin,
  MoreHorizontal,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Download,
  Upload,
  Share2,
  Copy,
  Trash2,
  RefreshCw,
  RotateCcw,
  RotateCw,
  Maximize,
  Minimize,
  Move,
  Grip,
  Grid,
  List,
  Layout,
  Sidebar,
  Menu,
  X as XIcon,
  CheckCircle,
  AlertTriangle,
  Info as InfoIcon,
  HelpCircle as HelpIcon,
  MessageSquare,
  MessageCircle,
  Send,
  Reply,
  Forward,
  Flag,
  Bookmark,
  BookmarkCheck,
  Calendar as CalendarIcon,
  Clock,
  Timer,
  Stopwatch,
  History,
  Activity,
  Pulse,
  Heart as HeartIcon,
  Smile,
  Frown,
  Meh,
  ThumbsUp,
  ThumbsDown,
  Hand,
  HandHeart,
  Handshake,
  User,
  UserCheck,
  UserX,
  UserPlus,
  UserMinus,
  Users as UsersIcon,
  UserCog,
  UserEdit,
  UserSearch,
  UserShield,
  UserStar,
  UserTag,
  UserTie,
  UserVoice,
  UserWrench,
  UserZap,
  Building,
  Building2,
  Home as HomeIcon,
  House,
  Warehouse,
  Store,
  Shop,
  ShoppingBag,
  ShoppingCart,
  CreditCard as CreditCardIcon,
  Banknote,
  Coins,
  DollarSign,
  Euro,
  PoundSterling,
  Yen,
  Bitcoin,
  Wallet,
  Receipt,
  FileText as FileTextIcon,
  File,
  FileCheck,
  FileX,
  FileEdit,
  FileImage,
  FileVideo,
  FileAudio,
  FilePdf,
  FileWord,
  FileExcel,
  FilePowerpoint,
  FileArchive,
  FileCode,
  FileJson,
  FileXml,
  FileCsv,
  FileDatabase,
  FileSpreadsheet,
  FileText as FileTextIcon2,
  Folder,
  FolderOpen,
  FolderPlus,
  FolderMinus,
  FolderX,
  FolderCheck,
  FolderEdit,
  FolderLock,
  FolderUnlock,
  FolderHeart,
  FolderStar,
  FolderUser,
  FolderSettings,
  FolderCog,
  FolderWrench,
  FolderZap,
  FolderShield,
  FolderKey,
  FolderLock as FolderLockIcon,
  FolderUnlock as FolderUnlockIcon,
  FolderHeart as FolderHeartIcon,
  FolderStar as FolderStarIcon,
  FolderUser as FolderUserIcon,
  FolderSettings as FolderSettingsIcon,
  FolderCog as FolderCogIcon,
  FolderWrench as FolderWrenchIcon,
  FolderZap as FolderZapIcon,
  FolderShield as FolderShieldIcon,
  FolderKey as FolderKeyIcon
} from 'lucide-react';

interface ProfileStats {
  totalLessons: number;
  completedLessons: number;
  currentStreak: number;
  totalPoints: number;
  achievements: number;
  reflections: number;
  goals: number;
  challenges: number;
}

interface NotificationSettings {
  email: boolean;
  push: boolean;
  sms: boolean;
  weeklyDigest: boolean;
  milestoneUpdates: boolean;
  newContent: boolean;
  communityUpdates: boolean;
  systemUpdates: boolean;
}

interface PrivacySettings {
  profileVisibility: 'public' | 'private' | 'friends';
  showProgress: boolean;
  showAchievements: boolean;
  showReflections: boolean;
  allowMessages: boolean;
  dataSharing: boolean;
  analytics: boolean;
}

interface SecuritySettings {
  twoFactorAuth: boolean;
  loginNotifications: boolean;
  sessionTimeout: number;
  passwordExpiry: number;
  deviceManagement: boolean;
  auditLog: boolean;
}

const ProfileSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, refreshProfileStatus } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'settings' | 'security' | 'notifications' | 'privacy'>('profile');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showLearningStyleModal, setShowLearningStyleModal] = useState(false);
  const [learningStyle, setLearningStyle] = useState('visual');
  const [profileStats, setProfileStats] = useState<ProfileStats | null>(null);
  
  const [data, setData] = useState<OnboardingData>({
    userType: 'immigrant',
    firstName: '',
    lastName: '',
    preferredLanguage: 'en',
    zipCode: '',
    maritalStatus: 'Single',
    dependents: 0,
    employmentStatus: 'Not working yet',
    lifeGoals: [],
    interests: [],
    currentGoals: [],
  });

  const [documentStatus, setDocumentStatus] = useState({
    hasSsn: false,
    hasPhone: false,
    hasHousing: false,
    ssnLastFour: '',
    idLastFour: '',
    phoneNumber: '',
    streetAddress: ''
  });

  const [preferences, setPreferences] = useState({
    language: 'en',
    notifications: true,
    darkMode: false,
    autoAdvance: false,
    showProgress: true,
    culturalContext: true
  });

  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>({
    email: true,
    push: true,
    sms: false,
    weeklyDigest: true,
    milestoneUpdates: true,
    newContent: true,
    communityUpdates: false,
    systemUpdates: true
  });

  const [privacySettings, setPrivacySettings] = useState<PrivacySettings>({
    profileVisibility: 'private',
    showProgress: true,
    showAchievements: true,
    showReflections: false,
    allowMessages: true,
    dataSharing: false,
    analytics: true
  });

  const [securitySettings, setSecuritySettings] = useState<SecuritySettings>({
    twoFactorAuth: false,
    loginNotifications: true,
    sessionTimeout: 30,
    passwordExpiry: 90,
    deviceManagement: true,
    auditLog: true
  });

  // SEO optimization
  useSEO({
    title: 'My Profile & Settings - MyNewStart',
    description: 'Manage your profile, preferences, and account settings for your American journey.',
    keywords: ['profile', 'settings', 'preferences', 'account', 'personal information'],
    breadcrumbs: [
      { name: 'Home', url: '/' },
      { name: 'Dashboard', url: '/dashboard' },
      { name: 'My Profile & Settings', url: '/profile-settings' }
    ]
  });

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }

    loadProfileData();
  }, [user, navigate]);

  const loadProfileData = async () => {
    try {
      setInitialLoading(true);
      
      // Load profile data
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;

      if (profile) {
        setData({
          userType: profile.user_type || 'immigrant',
          firstName: profile.first_name || '',
          lastName: profile.last_name || '',
          preferredLanguage: profile.preferred_language || 'en',
          zipCode: profile.zip_code || '',
          maritalStatus: profile.marital_status || 'Single',
          dependents: profile.dependents || 0,
          employmentStatus: profile.employment_status || 'Not working yet',
          lifeGoals: profile.life_goals || [],
          interests: profile.interests || [],
          currentGoals: profile.current_goals || [],
          otherGoal: profile.other_goal || '',
          concerns: profile.concerns || ''
        });
        
        setDocumentStatus({
          hasSsn: profile.has_ssn || false,
          hasPhone: profile.has_phone || false,
          hasHousing: profile.has_housing || false,
          ssnLastFour: profile.ssn_last_four || '',
          idLastFour: profile.id_last_four || '',
          phoneNumber: profile.phone_number || '',
          streetAddress: profile.street_address || ''
        });
      }

      // Load profile statistics
      const mockStats: ProfileStats = {
        totalLessons: 24,
        completedLessons: 18,
        currentStreak: 7,
        totalPoints: 1250,
        achievements: 8,
        reflections: 12,
        goals: 5,
        challenges: 3
      };
      setProfileStats(mockStats);

    } catch (err) {
      console.error('Error loading profile data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load profile data');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const { error } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          user_type: data.userType,
          first_name: data.firstName,
          last_name: data.lastName,
          preferred_language: data.preferredLanguage,
          zip_code: data.zipCode,
          marital_status: data.maritalStatus,
          dependents: data.dependents,
          employment_status: data.employmentStatus,
          life_goals: data.lifeGoals,
          interests: data.interests,
          current_goals: data.currentGoals,
          other_goal: data.otherGoal,
          concerns: data.concerns,
          has_ssn: documentStatus.hasSsn,
          has_phone: documentStatus.hasPhone,
          has_housing: documentStatus.hasHousing,
          ssn_last_four: documentStatus.ssnLastFour,
          id_last_four: documentStatus.idLastFour,
          phone_number: documentStatus.phoneNumber,
          street_address: documentStatus.streetAddress,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      setSuccess('Profile updated successfully!');
      refreshProfileStatus();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);

    } catch (err) {
      console.error('Error saving profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const handleLearningStyleComplete = (style: string, data: any) => {
    setLearningStyle(style);
    setShowLearningStyleModal(false);
    console.log('Learning style updated:', style, data);
  };

  const handlePreferenceChange = (key: string, value: any) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
  };

  const handleNotificationChange = (key: keyof NotificationSettings, value: boolean) => {
    setNotificationSettings(prev => ({ ...prev, [key]: value }));
  };

  const handlePrivacyChange = (key: keyof PrivacySettings, value: any) => {
    setPrivacySettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSecurityChange = (key: keyof SecuritySettings, value: any) => {
    setSecuritySettings(prev => ({ ...prev, [key]: value }));
  };

  const tabs = [
    { id: 'profile', name: 'Profile', icon: UserCircle },
    { id: 'settings', name: 'Settings', icon: Settings },
    { id: 'security', name: 'Security', icon: Shield },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'privacy', name: 'Privacy', icon: Eye }
  ];

  if (initialLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          <UserCircle className="w-10 h-10 inline-block mr-3 text-blue-600" />
          My Profile & Settings
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Manage your profile, preferences, and account settings for your American journey.
        </p>
      </div>

      {/* Profile Stats */}
      {profileStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <BookOpen className="w-6 h-6 text-blue-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">
              <AnimatedCounter value={profileStats.completedLessons} />
            </div>
            <p className="text-sm text-gray-600">Completed Lessons</p>
          </div>

          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 text-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">
              <AnimatedCounter value={profileStats.currentStreak} />
            </div>
            <p className="text-sm text-gray-600">Day Streak</p>
          </div>

          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <Star className="w-6 h-6 text-purple-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">
              <AnimatedCounter value={profileStats.totalPoints} />
            </div>
            <p className="text-sm text-gray-600">Total Points</p>
          </div>

          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 text-center">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <Award className="w-6 h-6 text-orange-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">
              <AnimatedCounter value={profileStats.achievements} />
            </div>
            <p className="text-sm text-gray-600">Achievements</p>
          </div>
        </div>
      )}

      {/* Error/Success Messages */}
      {error && (
        <Alert type="error" className="mb-6">
          {error}
        </Alert>
      )}
      {success && (
        <Alert type="success" className="mb-6">
          {success}
        </Alert>
      )}

      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-2 mb-8">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex items-center px-4 py-2 rounded-lg border transition-all duration-200 ${
              activeTab === tab.id
                ? 'bg-blue-100 border-blue-500 text-blue-700'
                : 'bg-white border-gray-300 text-gray-600 hover:border-blue-300 hover:text-blue-600'
            }`}
          >
            <tab.icon className="w-4 h-4 mr-2" />
            {tab.name}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-8">
        {activeTab === 'profile' && (
          <div className="space-y-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Profile Information</h2>
            
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  value={data.firstName}
                  onChange={(e) => setData(prev => ({ ...prev, firstName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  value={data.lastName}
                  onChange={(e) => setData(prev => ({ ...prev, lastName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country of Origin
                </label>
                <input
                  type="text"
                  value={data.countryOfOrigin}
                  onChange={(e) => setData(prev => ({ ...prev, countryOfOrigin: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Arrival Year
                </label>
                <input
                  type="number"
                  value={data.arrivalYear}
                  onChange={(e) => setData(prev => ({ ...prev, arrivalYear: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Zip Code
                </label>
                <input
                  type="text"
                  value={data.zipCode}
                  onChange={(e) => setData(prev => ({ ...prev, zipCode: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Employment Status
                </label>
                <select
                  value={data.employmentStatus}
                  onChange={(e) => setData(prev => ({ ...prev, employmentStatus: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Not working yet">Not working yet</option>
                  <option value="Part-time">Part-time</option>
                  <option value="Full-time">Full-time</option>
                  <option value="Self-employed">Self-employed</option>
                  <option value="Student">Student</option>
                  <option value="Retired">Retired</option>
                </select>
              </div>
            </div>

            {/* Document Status */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Document Status</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={documentStatus.hasSsn}
                    onChange={(e) => setDocumentStatus(prev => ({ ...prev, hasSsn: e.target.checked }))}
                    className="mr-2"
                  />
                  <label className="text-sm font-medium text-gray-700">Has SSN</label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={documentStatus.hasPhone}
                    onChange={(e) => setDocumentStatus(prev => ({ ...prev, hasPhone: e.target.checked }))}
                    className="mr-2"
                  />
                  <label className="text-sm font-medium text-gray-700">Has Phone</label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={documentStatus.hasHousing}
                    onChange={(e) => setDocumentStatus(prev => ({ ...prev, hasHousing: e.target.checked }))}
                    className="mr-2"
                  />
                  <label className="text-sm font-medium text-gray-700">Has Housing</label>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Preferences & Settings</h2>
            
            {/* Learning Preferences */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Learning Preferences</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div>
                    <h4 className="font-medium text-gray-900">Learning Style</h4>
                    <p className="text-sm text-gray-600 capitalize">
                      Current: {learningStyle.replace('_', ' ')} learner
                    </p>
                  </div>
                  <ModernButton
                    variant="outline"
                    size="sm"
                    onClick={() => setShowLearningStyleModal(true)}
                  >
                    Assess Style
                  </ModernButton>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900">Auto Advance</h4>
                    <p className="text-sm text-gray-600">Automatically move to next lesson</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.autoAdvance}
                      onChange={(e) => handlePreferenceChange('autoAdvance', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900">Show Progress</h4>
                    <p className="text-sm text-gray-600">Display progress indicators</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.showProgress}
                      onChange={(e) => handlePreferenceChange('showProgress', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900">Cultural Context</h4>
                    <p className="text-sm text-gray-600">Include cultural explanations</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.culturalContext}
                      onChange={(e) => handlePreferenceChange('culturalContext', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </div>

            {/* Language Preferences */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Language Preferences</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Language
                </label>
                <select
                  value={preferences.language}
                  onChange={(e) => handlePreferenceChange('language', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                  <option value="de">German</option>
                  <option value="it">Italian</option>
                  <option value="pt">Portuguese</option>
                  <option value="ru">Russian</option>
                  <option value="zh">Chinese</option>
                  <option value="ja">Japanese</option>
                  <option value="ko">Korean</option>
                  <option value="ar">Arabic</option>
                  <option value="hi">Hindi</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'notifications' && (
          <div className="space-y-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Notification Settings</h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">Email Notifications</h4>
                  <p className="text-sm text-gray-600">Receive updates via email</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationSettings.email}
                    onChange={(e) => handleNotificationChange('email', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">Push Notifications</h4>
                  <p className="text-sm text-gray-600">Receive push notifications</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationSettings.push}
                    onChange={(e) => handleNotificationChange('push', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">Weekly Digest</h4>
                  <p className="text-sm text-gray-600">Receive weekly summary emails</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationSettings.weeklyDigest}
                    onChange={(e) => handleNotificationChange('weeklyDigest', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">Milestone Updates</h4>
                  <p className="text-sm text-gray-600">Get notified of milestone achievements</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationSettings.milestoneUpdates}
                    onChange={(e) => handleNotificationChange('milestoneUpdates', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'privacy' && (
          <div className="space-y-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Privacy Settings</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Profile Visibility
                </label>
                <select
                  value={privacySettings.profileVisibility}
                  onChange={(e) => handlePrivacyChange('profileVisibility', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                  <option value="friends">Friends Only</option>
                </select>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">Show Progress</h4>
                  <p className="text-sm text-gray-600">Allow others to see your progress</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privacySettings.showProgress}
                    onChange={(e) => handlePrivacyChange('showProgress', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">Show Achievements</h4>
                  <p className="text-sm text-gray-600">Allow others to see your achievements</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privacySettings.showAchievements}
                    onChange={(e) => handlePrivacyChange('showAchievements', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">Allow Messages</h4>
                  <p className="text-sm text-gray-600">Allow other users to message you</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privacySettings.allowMessages}
                    onChange={(e) => handlePrivacyChange('allowMessages', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'security' && (
          <div className="space-y-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Security Settings</h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">Two-Factor Authentication</h4>
                  <p className="text-sm text-gray-600">Add an extra layer of security</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={securitySettings.twoFactorAuth}
                    onChange={(e) => handleSecurityChange('twoFactorAuth', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">Login Notifications</h4>
                  <p className="text-sm text-gray-600">Get notified of new logins</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={securitySettings.loginNotifications}
                    onChange={(e) => handleSecurityChange('loginNotifications', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Session Timeout (minutes)
                </label>
                <input
                  type="number"
                  value={securitySettings.sessionTimeout}
                  onChange={(e) => handleSecurityChange('sessionTimeout', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password Expiry (days)
                </label>
                <input
                  type="number"
                  value={securitySettings.passwordExpiry}
                  onChange={(e) => handleSecurityChange('passwordExpiry', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="mt-8 flex justify-end">
          <ModernButton
            onClick={handleSave}
            loading={loading}
            className="px-8 py-3"
          >
            <Save className="w-4 h-4 mr-2" />
            Save Changes
          </ModernButton>
        </div>
      </div>

      {/* Learning Style Assessment Modal */}
      {showLearningStyleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Learning Style Assessment</h3>
                <button
                  onClick={() => setShowLearningStyleModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XIcon className="w-6 h-6" />
                </button>
              </div>
              <LearningStyleAssessment
                onComplete={handleLearningStyleComplete}
                onCancel={() => setShowLearningStyleModal(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Call to Action */}
      <div className="mt-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl p-8 text-center text-white">
        <h2 className="text-2xl font-bold mb-4">Complete Your Profile</h2>
        <p className="text-lg mb-6 opacity-90">
          A complete profile helps us provide you with personalized guidance and recommendations.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => navigate('/guides')}
            className="btn bg-white text-blue-600 hover:bg-gray-100"
          >
            Explore Guides
          </button>
          <button
            onClick={() => navigate('/next-steps')}
            className="btn border-2 border-white text-white hover:bg-white hover:text-blue-600"
          >
            View Next Steps
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettingsPage;
